/**
 * Scene-level TTS Dialog Generation — parse screenplay dialog, assign voices,
 * batch-generate audio, review, and place on the Dialog lane.
 * Uses shared AiDialogShell (modal chrome + footer) and useAiDialogFlow (step machine).
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useAudioStore } from '@/store/audioStore.ts';
import { useAiStore } from '@/store/aiStore.ts';
import { useUndoStore } from '@/store/undoStore.ts';
import { reapplyHistoryEntry } from '@/store/actions.ts';
import { FALLBACK_CATALOG, getModelEstSeconds, recordGenerationTime } from '@/ai/tokenCosts.ts';
import { AiDialogShell } from './AiDialogShell.tsx';
import { CostBanner, formatTimeEstimate } from './CostBanner.tsx';
import { GenerationProgress } from './GenerationProgress.tsx';
import { GenerationHistoryPanel } from './GenerationHistoryPanel.tsx';
import { useAiDialogFlow } from '@/hooks/useAiDialogFlow.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { describeAiError } from '@/ai/aiErrorMessages.ts';
import { resolveClipPlacement } from '@/store/projectDerived.ts';
import { showToast } from './Toast.tsx';
import { parseScreenplayDialog, extractCharacters } from '@/previs/screenplayParser.ts';
import { structuredToDialogLines, extractStructuredCharacters } from '@/previs/screenplayHelpers.ts';
import { getVoicesForModel, buildVoiceMap, TTS_COSTS } from '@/types/tts.ts';
import { splitCredits } from './AudioWorkspace/scratchCredits.ts';
import { ModelPicker } from './ModelPicker.tsx';
import type { DialogLine } from '@/previs/screenplayParser.ts';
import type { TtsVoicePreset } from '@/types/tts.ts';
import type { GenerationHistoryEntry } from '@/types/generationHistory.ts';
import styles from './AiDialog.module.css';

interface TtsReviewItem {
  id: string;
  index: number;
  character: string;
  text: string;
  success: boolean;
  audioUrl?: string;
  cdnUrl?: string;   // fal.ai CDN URL (for native download by Unity)
  duration?: number;
  accepted: boolean;
  shotId?: number;  // from structured screenplay, for retime placement
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function AiTtsDialog({ open, onClose }: Props) {
  const { t } = useTranslation('aiTts');
  const project = useProjectStore(s => s.project);
  const activeSceneId = useProjectStore(s => s.activeSceneId);
  const _allocId = useProjectStore(s => s._allocId);
  const updateShotDuration = useProjectStore(s => s.updateShotDuration);
  const addAudioClip = useAudioStore(s => s.addAudioClip);
  const catalog = useAiStore(s => s.aiModelCatalog);
  const voiceAssignments = useAiStore(s => s.voiceAssignments);
  const setVoiceAssignment = useAiStore(s => s.setVoiceAssignment);
  const addHistoryEntry = useAiStore(s => s.addHistoryEntry);
  const retimeToMatchSpeech = useAiStore(s => s.retimeToMatchSpeech);
  const toggleRetimeToMatchSpeech = useAiStore(s => s.toggleRetimeToMatchSpeech);

  // ── History picker ──────────────────────────────────────────────────
  const [historyPickerOpen, setHistoryPickerOpen] = useState(false);

  const handleHistoryPick = useCallback((entry: GenerationHistoryEntry) => {
    reapplyHistoryEntry(entry.id, 0);
    setHistoryPickerOpen(false);
    onClose();
  }, [onClose]);

  const effectiveCatalog = catalog.length > 0 ? catalog : FALLBACK_CATALOG;
  const [modelId, setModelId] = useState('kokoro');
  const totalCost = TTS_COSTS[modelId]?.perScene ?? 10;

  // TTS doesn't use prompt, but useAiDialogFlow requires it
  const {
    step, setStep, elapsedSeconds,
    progress, setProgress, completedMedia, setCompletedMedia,
    reviewItems, setReviewItems, acceptedCount,
    startGeneration, handleCancel, handleAcceptAll, handleRejectAll, handleToggleItem,
  } = useAiDialogFlow<TtsReviewItem>({ open, defaultPrompt: '' });

  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Reset on close — revoke blob URLs from unapplied review items
  useEffect(() => {
    if (!open) {
      setReviewItems(prev => {
        for (const item of prev) {
          if (item.audioUrl) try { URL.revokeObjectURL(item.audioUrl); } catch { /* ok */ }
        }
        return [];
      });
      setPlayingIndex(null);
    }
  }, [open, setReviewItems]);

  // ── Scene data ───────────────────────────────────────────────────────
  const currentTime = usePlaybackStore(s => s.currentTime);
  const getShotAtTime = useProjectStore(s => s.getShotAtTime);
  const allScenes = project?.scenes ?? [];
  const currentScene = useMemo(() => {
    // Prefer scene at current playhead position
    const shot = getShotAtTime(currentTime);
    if (shot) {
      const scene = allScenes.find(s => s.shots.some(sh => sh.id === shot.id));
      if (scene) return scene;
    }
    // Fall back to active scene filter, then first scene
    if (activeSceneId !== null) {
      return allScenes.find(s => s.id === activeSceneId) ?? allScenes[0];
    }
    return allScenes[0];
  }, [allScenes, activeSceneId, currentTime, getShotAtTime]);

  const dialogLines: DialogLine[] = useMemo(() => {
    if (!currentScene) return [];
    // Prefer structured data when available
    if (currentScene.screenplay?.dialogue.length) {
      return structuredToDialogLines(currentScene.screenplay);
    }
    // Fallback to raw text parsing
    return currentScene.screenplayText ? parseScreenplayDialog(currentScene.screenplayText) : [];
  }, [currentScene]);

  // Parallel array of shotIds for each dialog line (from structured data)
  const dialogShotIds: number[] = useMemo(() => {
    if (!currentScene?.screenplay?.dialogue.length) return [];
    return currentScene.screenplay.dialogue
      .slice()
      .sort((a, b) => a.scriptOrder - b.scriptOrder)
      .map(d => d.shotId);  // 0 = unassigned
  }, [currentScene]);

  const characters = useMemo(() => {
    if (currentScene?.screenplay?.dialogue.length) {
      return extractStructuredCharacters(currentScene.screenplay);
    }
    return extractCharacters(dialogLines);
  }, [currentScene, dialogLines]);

  // ── Voice assignments for current scene ──────────────────────────────
  const sceneVoices = useMemo(() => {
    if (!currentScene) return new Map<string, string>();
    return buildVoiceMap(currentScene.id, modelId, voiceAssignments, characters);
  }, [currentScene, voiceAssignments, characters, modelId]);

  const voices: TtsVoicePreset[] = useMemo(() => getVoicesForModel(modelId), [modelId]);

  const handleVoiceChange = useCallback((character: string, voice: string) => {
    if (!currentScene) return;
    setVoiceAssignment(currentScene.id, character, modelId, voice);
  }, [currentScene, modelId, setVoiceAssignment]);

  // ── Cost ──────────────────────────────────────────────────────────────
  const estSeconds = useMemo(() => {
    // Batch TTS runs all lines in parallel on the server.
    // Use observed batch times if available, otherwise estimate:
    // wall-clock ≈ ~10s server generation + ~1s per line download/decode.
    const observed = getModelEstSeconds(modelId, effectiveCatalog, 'batch');
    // getModelEstSeconds returns the catalog fallback (per-line) when no
    // batch observations exist. Detect that and use our batch formula instead.
    const catalogFallback = Math.round(
      (effectiveCatalog.find(m => m.id === modelId)?.estSeconds ?? 30) * 1.1,
    );
    if (observed !== catalogFallback) return observed;
    // No batch observations yet — flat estimate with small per-line buffer
    return Math.round((10 + Math.min(dialogLines.length, 15)) * 1.1);
  }, [dialogLines.length, modelId, effectiveCatalog]);

  // ── Generate ─────────────────────────────────────────────────────────
  const handleGenerate = useCallback(async () => {
    if (!currentScene || dialogLines.length === 0) return;

    const abort = startGeneration();
    const genStartTime = performance.now();

    try {
      const lines = dialogLines.map(dl => ({
        text: dl.text,
        voice: sceneVoices.get(dl.character) || voices[0]?.id || 'af_heart',
        character: dl.character,
      }));

      const { generateSceneTts } = await import('@/ai/ttsOrchestrator.ts');
      const completedMap = new Map<string, string>();

      const result = await generateSceneTts({
        dialogLines: lines,
        model: modelId,
        onProgress: (current, total) => {
          setProgress({ current, total, message: `${current}/${total} lines` });
          // Mark items as completed for the progress grid
          for (let i = 0; i < current; i++) {
            completedMap.set(String(i), 'completed');
          }
          setCompletedMedia(new Map(completedMap));
        },
        abortSignal: abort.signal,
      });

      // Build review items
      const items: TtsReviewItem[] = result.results.map((r, i) => ({
        id: String(i),
        index: i,
        character: r.character,
        text: r.text,
        success: r.success,
        audioUrl: r.audioUrl,
        cdnUrl: r.cdnUrl,
        duration: r.duration,
        accepted: r.success,
        shotId: dialogShotIds[i] || undefined,
      }));

      setReviewItems(items);
      recordGenerationTime(modelId, (performance.now() - genStartTime) / 1000, 'batch');
      setStep('review');
    } catch (err) {
      if (abort.signal.aborted) {
        setStep('options');
        return;
      }
      console.error('TTS generation failed:', err);
      useUiStore.getState().showAiError(t('speechGenerationStopped'), describeAiError(err));
      setStep('options');
    }
  }, [currentScene, dialogLines, dialogShotIds, sceneVoices, voices, modelId, startGeneration, setProgress, setCompletedMedia, setReviewItems, setStep, t]);

  // Play preview
  const handlePlay = useCallback((index: number, audioUrl?: string) => {
    if (!audioUrl) return;
    if (playingIndex === index) {
      audioRef.current?.pause();
      setPlayingIndex(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(audioUrl);
    audio.onended = () => setPlayingIndex(null);
    audio.play();
    audioRef.current = audio;
    setPlayingIndex(index);
  }, [playingIndex]);

  // ── Apply ────────────────────────────────────────────────────────────
  const handleApply = useCallback(async () => {
    if (!currentScene) return;

    useUndoStore.getState().pushSnapshot();

    const { createTtsAudioClip, createTtsHistoryEntry } = await import('@/ai/ttsOrchestrator.ts');

    // Distribute batch cost across accepted lines.
    // 20261008 (owner): the scene is billed ONE flat perScene batch (supabase/functions/ai-generate/
    // _tts.ts), so the per-clip history costs must sum to exactly that — Math.round(total / n)
    // drifted (10 over 3 lines logged 9, 15 over 4 logged 16). splitCredits gives [4, 3, 3].
    const acceptedLines = reviewItems.filter(i => i.accepted && i.success && i.audioUrl);
    const clipCosts = splitCredits(totalCost, acceptedLines.length);
    const clipCostAt = (index: number) => clipCosts[index] ?? 0;

    // Pre-decode all accepted items
    const decoded: Array<{
      item: TtsReviewItem;
      resultLike: { audioUrl: string; cdnUrl?: string; duration: number; rawData?: Uint8Array; audioBuffer?: AudioBuffer; peaks?: Float32Array; success: true; character: string; text: string; seed: null };
    }> = [];

    for (const item of reviewItems) {
      if (!item.accepted || !item.success || !item.audioUrl) continue;

      const resultLike = {
        audioUrl: item.audioUrl,
        cdnUrl: item.cdnUrl,
        duration: item.duration ?? 0,
        rawData: undefined as Uint8Array | undefined,
        audioBuffer: undefined as AudioBuffer | undefined,
        peaks: undefined as Float32Array | undefined,
        success: true as const,
        character: item.character,
        text: item.text,
        seed: null,
      };

      try {
        const resp = await fetch(item.audioUrl);
        const buf = await resp.arrayBuffer();
        const rawData = new Uint8Array(buf);
        const { decodeAudio, generatePeaks } = await import('@/audio/audioProcessor.ts');
        const audioBuffer = await decodeAudio(rawData);
        const peaks = generatePeaks(audioBuffer);
        resultLike.rawData = rawData;
        resultLike.audioBuffer = audioBuffer;
        resultLike.peaks = peaks;
        resultLike.duration = audioBuffer.duration;
      } catch (err) {
        console.warn('Failed to decode TTS audio for clip placement:', err);
      }

      decoded.push({ item, resultLike });
    }

    const PADDING = 1.0; // 1s breathing room before & after speech in each shot
    const GAP = 0.3;     // gap between consecutive clips

    // ── Calculate scene start time ────────────────────────────────────
    let sceneStartTime = 0;
    for (const s of allScenes) {
      if (s.id === currentScene.id) break;
      sceneStartTime += s.shots.reduce((sum, sh) => sum + sh.duration, 0);
    }

    let applied = 0;

    if (retimeToMatchSpeech) {
      // ── Retime approach: assign clips to shots, extend shots, then place ──
      const shots = currentScene.shots;
      type ShotBucket = { shotId: number; origDuration: number; clips: typeof decoded };
      const buckets: ShotBucket[] = shots.map(s => ({
        shotId: s.id,
        origDuration: s.duration,
        clips: [],
      }));

      // Distribute clips across shots: fill each shot until speech overflows
      let bucketIdx = 0;
      let usedInBucket = PADDING; // start with leading padding
      for (const entry of decoded) {
        const clipDur = entry.resultLike.duration || 2;

        // Would this clip + trailing padding fit in the current shot?
        const wouldNeed = usedInBucket + clipDur + PADDING;

        if (buckets[bucketIdx].clips.length > 0 && wouldNeed > buckets[bucketIdx].origDuration && bucketIdx < buckets.length - 1) {
          // Overflow — move to next shot
          bucketIdx++;
          usedInBucket = PADDING; // reset with leading padding
        }

        buckets[bucketIdx].clips.push(entry);
        usedInBucket += clipDur + GAP;
      }

      // Extend shots whose dialog overflows their original duration
      for (const bucket of buckets) {
        if (bucket.clips.length === 0) continue;
        let needed = PADDING; // leading padding
        for (let i = 0; i < bucket.clips.length; i++) {
          needed += bucket.clips[i].resultLike.duration || 2;
          if (i < bucket.clips.length - 1) needed += GAP;
        }
        needed += PADDING; // trailing padding

        if (needed > bucket.origDuration) {
          updateShotDuration(bucket.shotId, needed, { pushUndo: false });
          bucket.origDuration = needed; // track updated duration for placement
        }
      }

      // Recalculate scene start (earlier scenes unchanged, but be safe)
      const freshScenes = useProjectStore.getState().project?.scenes ?? [];
      let sStart = 0;
      for (const s of freshScenes) {
        if (s.id === currentScene.id) break;
        sStart += s.shots.reduce((sum, sh) => sum + sh.duration, 0);
      }

      // Place clips at final absolute positions within each shot
      let shotAbsStart = sStart;
      for (const bucket of buckets) {
        // Re-read shot duration (may have been extended)
        const freshShot = freshScenes.find(s => s.id === currentScene.id)?.shots.find(sh => sh.id === bucket.shotId);
        const shotDur = freshShot?.duration ?? bucket.origDuration;

        let cursor = shotAbsStart + PADDING;
        for (const { item, resultLike } of bucket.clips) {
          const clipId = _allocId();
          const clip = createTtsAudioClip(
            resultLike as any,
            bucket.shotId,
            cursor - shotAbsStart,
            clipId,
            item.character,
            item.text,
          );
          addAudioClip(clip, { pushUndo: false });

          const histEntry = createTtsHistoryEntry(
            resultLike as any,
            bucket.shotId,
            modelId,
            sceneVoices.get(item.character) || '',
            item.character,
            item.text,
            clipCostAt(applied),
            clipId,
          );
          addHistoryEntry(histEntry);

          if (resultLike.cdnUrl) {
            import('@/bridge/generationHistorySyncBridge.ts').then(m => {
              m.sendHistoryCdnUrlToUnity(histEntry.id, resultLike.cdnUrl!, 'audio/mpeg');
            });
          }

          cursor += (resultLike.duration || 2) + GAP;
          applied++;
        }

        shotAbsStart += shotDur;
      }
    } else {
      // ── No retime: place clips sequentially from scene start ─────────
      const noRetimeProject = useProjectStore.getState().project;
      let placementTime = sceneStartTime;
      for (const { item, resultLike } of decoded) {
        const clipId = _allocId();

        const { shotId: nrShotId, shotOffset: nrShotOffset } = noRetimeProject
          ? resolveClipPlacement(placementTime, noRetimeProject)
          : { shotId: 0, shotOffset: placementTime };
        const clip = createTtsAudioClip(
          resultLike as any,
          nrShotId,
          nrShotOffset,
          clipId,
          item.character,
          item.text,
        );
        addAudioClip(clip, { pushUndo: false });

        const histEntry = createTtsHistoryEntry(
          resultLike as any,
          null,
          modelId,
          sceneVoices.get(item.character) || '',
          item.character,
          item.text,
          clipCostAt(applied),
          clipId,
        );
        addHistoryEntry(histEntry);

        if (resultLike.cdnUrl) {
          import('@/bridge/generationHistorySyncBridge.ts').then(m => {
            m.sendHistoryCdnUrlToUnity(histEntry.id, resultLike.cdnUrl!, 'audio/mpeg');
          });
        }

        placementTime += (resultLike.duration || 2) + GAP;
        applied++;
      }
    }

    // Revoke blob URLs — audio data has been decoded into AudioBuffers
    for (const { item } of decoded) {
      if (item.audioUrl) try { URL.revokeObjectURL(item.audioUrl); } catch { /* ok */ }
    }

    showToast(t('dialogClipsAddedToast', { count: applied, plural: applied !== 1 ? 's' : '' }));
    setStep('done');
  }, [reviewItems, currentScene, allScenes, _allocId, addAudioClip, addHistoryEntry, modelId, sceneVoices, retimeToMatchSpeech, updateShotDuration, setStep, t]);

  // ── Progress items ───────────────────────────────────────────────────
  const progressItems = useMemo(() =>
    dialogLines.map((dl, i) => ({
      id: String(i),
      completed: completedMedia.has(String(i)),
      character: dl.character,
    })),
    [dialogLines, completedMedia],
  );

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <>
    <AiDialogShell
      open={open}
      onClose={onClose}
      title={t('generateSpeechTitle')}
      step={step}
      wide
      totalTokenCost={totalCost}
      generateLabel={t('generateSpeechButton', { cost: totalCost })}
      generateDisabled={dialogLines.length === 0}
      onGenerate={handleGenerate}
      onSettingsDone={onClose}
      actionVerb={t('actionVerbGenerate')}
      estTotalSeconds={estSeconds}
      elapsedSeconds={elapsedSeconds}
      onCancel={handleCancel}
      acceptedCount={acceptedCount}
      applyNoun={t('applyNounClip')}
      onApply={handleApply}
      onLoadFromHistory={() => setHistoryPickerOpen(true)}
    >
      {/* ── Options step ──────────────────────────────────────────── */}
      {step === 'options' && (
        <>
          {dialogLines.length === 0 ? (
            <div className={styles.costBanner}>
              <div className={styles.costBannerTop}>
                {t('noDialogFound', { sceneName: currentScene?.name || t('untitledScene') })}
              </div>
            </div>
          ) : (
            <div className={styles.stepsGrid}>
              {/* Column 1: Scene info */}
              <div className={styles.stepColumn}>
                <div className={styles.stepHeader}>
                  <span className={styles.stepCircle}>1</span>
                  <span className={styles.stepLabel}>{t('stepLabelScene')}</span>
                </div>
                <div style={{
                  padding: '10px 12px',
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: 6,
                  fontSize: 13,
                  color: 'var(--text-primary)',
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    {currentScene?.name || t('untitledScene')}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted, #777)' }}>
                    {t('dialogLineCount', { count: dialogLines.length, plural: dialogLines.length !== 1 ? 's' : '' })}
                    {characters.length > 0 && ` \u00b7 ${t('characterCount', { count: characters.length, plural: characters.length !== 1 ? 's' : '' })}`}
                  </div>
                </div>
              </div>

              <div className={styles.stepArrow}>&#8250;</div>

              {/* Column 2: Voice */}
              <div className={styles.stepColumn}>
                <div className={styles.stepHeader}>
                  <span className={styles.stepCircle}>2</span>
                  <span className={styles.stepLabel}>{t('stepLabelVoice')}</span>
                </div>
                <ModelPicker compact category="tts" selectedModelId={modelId} onSelect={setModelId} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10, maxHeight: 160, overflowY: 'auto' }}>
                  {characters.map(character => (
                    <div key={character} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '5px 8px',
                      background: 'rgba(255,255,255,0.04)',
                      borderRadius: 4,
                    }}>
                      <span style={{
                        fontWeight: 600, fontSize: 11, color: 'var(--text-primary)',
                        textTransform: 'uppercase', minWidth: 70, maxWidth: 80,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {character}
                      </span>
                      <select
                        className={styles.select}
                        style={{ flex: 1, fontSize: 12, padding: '4px 6px' }}
                        value={sceneVoices.get(character) || ''}
                        onChange={e => handleVoiceChange(character, e.target.value)}
                      >
                        {voices.map(v => (
                          <option key={v.id} value={v.id}>
                            {v.name} ({v.gender})
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.stepArrow}>&#8250;</div>

              {/* Column 3: Settings */}
              <div className={styles.stepColumn}>
                <div className={styles.stepHeader}>
                  <span className={styles.stepCircle}>3</span>
                  <span className={styles.stepLabel}>{t('stepLabelSettings')}</span>
                </div>

                <label className={styles.checkboxRow}>
                  <input type="checkbox" checked={retimeToMatchSpeech} onChange={toggleRetimeToMatchSpeech} />
                  {t('retimeShotsToFitDialog')}
                </label>

                <CostBanner
                  credits={totalCost}
                  timeEstimate={formatTimeEstimate(estSeconds)}
                  extra={t('dialogLineCount', { count: dialogLines.length, plural: dialogLines.length !== 1 ? 's' : '' })}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Generating step ───────────────────────────────────────── */}
      {step === 'generating' && (
        <GenerationProgress
          current={progress?.current ?? 0}
          total={progress?.total ?? dialogLines.length}
          label={t('progressLabelLines')}
          message={progress?.message}
          items={progressItems}
          renderComplete={_item => (
            <div className={styles.shotGridPending} style={{ color: '#4caf50' }}>&#10003;</div>
          )}
          renderPending={item => {
            const pi = progressItems.find(p => p.id === item.id);
            return (
              <div className={styles.shotGridPending} style={{ fontSize: 8 }}>
                {pi?.character?.slice(0, 4) || (Number(item.id) + 1)}
              </div>
            );
          }}
        />
      )}

      {/* ── Review step ───────────────────────────────────────────── */}
      {step === 'review' && (
        <>
          <div className={styles.bulkActions}>
            <button className={styles.bulkBtn} onClick={handleAcceptAll}>{t('acceptAll')}</button>
            <button className={styles.bulkBtn} onClick={handleRejectAll}>{t('rejectAll')}</button>
          </div>
          <div className={styles.reviewGrid}>
            {reviewItems.map(item => (
              <div key={item.id} className={styles.reviewPair}>
                <div className={styles.reviewMedia} style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                  {item.success && item.audioUrl ? (
                    <button
                      onClick={() => handlePlay(item.index, item.audioUrl)}
                      style={{
                        width: 32, height: 32, borderRadius: '50%', border: 'none',
                        background: playingIndex === item.index ? 'var(--accent, #c43c2d)' : 'rgba(255,255,255,0.1)',
                        color: '#fff', cursor: 'pointer', fontSize: 14, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      {playingIndex === item.index ? '\u23F8' : '\u25B6'}
                    </button>
                  ) : (
                    <div style={{ width: 32, height: 32 }} />
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                      {item.character}
                    </div>
                    <div style={{
                      fontSize: 12, color: item.success ? 'var(--text-primary)' : '#e57373',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {item.success ? item.text : t('reviewFailedPrefix', { text: item.text })}
                    </div>
                    {item.duration != null && (
                      <div style={{ fontSize: 10, color: 'var(--text-muted, #777)' }}>
                        {item.duration.toFixed(1)}s
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.reviewActions}>
                  <button
                    className={`${styles.reviewToggle} ${item.accepted && item.success ? styles.reviewAccepted : styles.reviewRejected}`}
                    onClick={() => handleToggleItem(item.id)}
                    disabled={!item.success}
                  >
                    {item.accepted && item.success ? '\u2713' : '\u2715'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Done step ─────────────────────────────────────────────── */}
      {step === 'done' && (
        <div className={styles.doneSection}>
          <div className={styles.doneStat}>
            {t('dialogClipsPlaced', { count: acceptedCount, plural: acceptedCount !== 1 ? 's' : '' })}
          </div>
        </div>
      )}
    </AiDialogShell>

    {historyPickerOpen && (
      <GenerationHistoryPanel
        onPick={handleHistoryPick}
        onDismiss={() => setHistoryPickerOpen(false)}
        initialFilter="tts"
      />
    )}

    </>
  );
}
